<template>
  <EditorContent
    v-if="editor"
    :editor="editor"
    :class="[`tiptap-v4 text-start vp-${resolution}`, sid ? `--${sid}-text` : '']"
    @click.native="initer"
  />
</template>

<script>
import {
  Editor,
  EditorContent
} from '@tiptap/vue-2'

// Nodes
import {
  Dropcursor,
  Placeholder,
  Gapcursor
} from '@tiptap/extensions'
import Blockquote from '@tiptap/extension-blockquote'
import CodeBlock from '@tiptap/extension-code-block'
import Document from '@tiptap/extension-document'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import {
  BulletList,
  ListItem,
  OrderedList,
  TaskList,
  TaskItem
} from '@tiptap/extension-list'
import Paragraph from '@tiptap/extension-paragraph'
// import {TableKit} from '@tiptap/extension-table'
import Text from '@tiptap/extension-text'

// Marks / styles
import Bold from '@tiptap/extension-bold'
import Code from '@tiptap/extension-code'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import Strike from '@tiptap/extension-strike'
// import {
//   BackgroundColor,
//   Color,
//   FontFamily,
//   FontSize,
//   LineHeight,
//   TextStyleKit
// } from '@tiptap/extension-text-style'
import {FontWeight} from '@/plugins/TiptapV3FontWeight'
import {LetterSpacing} from '@/plugins/TiptapV3LetterSpacing'
import {TextIndent} from '@/plugins/TiptapV3TextIndent'
import {GradientColor} from '@/plugins/TiptapV3Gradient'
import {BgGradientColor} from '@/plugins/TiptapV3BgGradient'
import {BulletListStyle} from '@/plugins/TiptapV3ListStyle'
// import TextAlign from '@tiptap/extension-text-align'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Typography from '@tiptap/extension-typography'
import Underline from '@tiptap/extension-underline'

import TiptapGlobalVariable from '@/plugins/TiptapGlobalVariable'
import TiptapGlobalVariableLink from '@/plugins/TiptapGlobalVariableLink'

import {
  // Functionality
  ResponsiveEngine,

  // Marks
  TextAlign,
  BackgroundColor,
  Color,
  FontFamily,
  FontSize,
  LineHeight,
  TextStyleKit,

  // Nodes
  Image,
  Table,
  TableRow,
  TableHeader,
  TableCell
} from '@/plugins/tiptap-v3'

export default {
  name: 'ContentEditableV4',
  components: {
    EditorContent
  },
  props: {
    content: {
      type: String,
      required: true
    },
    sid: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    resolution: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      editor: null,
      editorToDestroy: null
    }
  },
  methods: {
    initer () {
      this.$store.commit('editor/SET_TIPTAP', this.editor)
      if (this.editor && this.editor.isEditable && !this.editor.view.hasFocus()) {
        this.editor.view.focus()
      }
    }
  },
  watch: {
    resolution (val) {
      if (!this.editor) return
      this.editor.commands.setResponsiveBreakpoint(val)
    }
  },
  mounted () {
    this.editor = new Editor({
      editable: !this.readonly,
      extensions: [
        // Core document
        Document,
        Paragraph.configure({
          HTMLAttributes: {
            class: 'mb-0'
          }
        }),
        Text,

        // UX helpers
        Dropcursor,
        Gapcursor,
        Placeholder.configure({
          placeholder: 'Start typing…'
        }),
        ResponsiveEngine,

        Image.configure({
          resize: {
            enabled: true,
            directions: [
              'top',
              'left',
              'right',
              'bottom',
              'top-left',
              'top-right',
              'bottom-left',
              'bottom-right'
            ],
            minWidth: 56,
            minHeight: 56
          }
        }),

        // Text styling / marks / links
        TextStyleKit,
        Color,
        GradientColor,
        BgGradientColor,
        BackgroundColor,
        FontFamily,
        FontSize,
        FontWeight,
        LetterSpacing,
        TextIndent.configure({
          types: ['paragraph', 'heading'],
          min: 0,
          max: 8,
          size: 2,
          unit: 'em'
        }),
        LineHeight,
        Bold,
        Italic,
        Strike,
        Subscript,
        Superscript,
        Underline,
        Code,
        Link,
        Typography,

        // Structure (block-level)
        Heading.configure({
          HTMLAttributes: {
            class: 'mb-0'
          },
          levels: [1, 2, 3, 4, 5, 6]
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph']
        }),
        Blockquote,
        HorizontalRule,
        HardBreak,

        // Lists
        BulletList,
        OrderedList,
        TaskList,
        TaskItem.configure({
          nested: true
        }),
        ListItem,
        BulletListStyle,

        // Code block
        CodeBlock,

        // Tables
        Table.configure({
          resizable: true,
          renderWrapper: true,
          lastColumnResizable: true,
          allowTableNodeSelection: false,
          handleWidth: 6,
          cellMinWidth: 80
        }),
        TableRow,
        TableHeader,
        TableCell,

        // Global Variables
        TiptapGlobalVariable,
        TiptapGlobalVariableLink
      ],
      onUpdate: ({ editor }) => {
        const content = editor.getHTML()
        this.$emit('input', content)
      },
      content: this.content
    })
    // Ensure responsive breakpoint is initialized for the first render
    this.editor.commands.setResponsiveBreakpoint(this.resolution)
  },
  beforeDestroy () {
    // Parent beforeDestroy runs before children; clear the prop so <EditorContent>
    // doesn't try to touch `editor.view.dom` during its own teardown.
    this.editorToDestroy = this.editor
    this.editor = null
    this.$store.commit('editor/SET_TIPTAP', null)
  },
  destroyed () {
    const editor = this.editorToDestroy
    this.editorToDestroy = null
    if (!editor) return

    try {
      editor.destroy()
    } catch (e) {
      // During very fast mount/unmount (e.g. duplicate/remove), tiptap may not have a mounted view.
      // Swallow only this known error; log anything else.
      const msg = e && typeof e.message === 'string' ? e.message : ''
      if (msg.includes('The editor view is not available')) return
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }
}
</script>

<style>
.tiptap-v4 {
  .tiptap.ProseMirror {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .tiptap.ProseMirror p {
    margin: 0;
  }

  .tiptap.ProseMirror p:not(:first-child):not(td p):not(th p) {
    margin-top: 20px;
  }

  .tiptap.ProseMirror h1,
  .tiptap.ProseMirror h2,
  .tiptap.ProseMirror h3,
  .tiptap.ProseMirror h4,
  .tiptap.ProseMirror h5,
  .tiptap.ProseMirror h6 {
    margin: 0;
  }

  .tiptap.ProseMirror h1:not(:first-child) { margin-top: 32px; }
  .tiptap.ProseMirror h2:not(:first-child) { margin-top: 28px; }
  .tiptap.ProseMirror h3:not(:first-child) { margin-top: 24px; }
  .tiptap.ProseMirror h4:not(:first-child),
  .tiptap.ProseMirror h5:not(:first-child),
  .tiptap.ProseMirror h6:not(:first-child)
  {
    margin-top: 20px;
  }

  .tiptap.ProseMirror ul,
  .tiptap.ProseMirror ol {
    padding-left: 1.5rem;
    margin: 0.5em 0;
  }

  .tiptap.ProseMirror li {
    margin: 0.25em 0;
  }

  .tiptap.ProseMirror ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;
  }

  .tiptap.ProseMirror ul[data-type="taskList"] li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .tiptap.ProseMirror ul[data-type="taskList"] li input {
    margin-top: .375rem;
  }

  .tiptap.ProseMirror blockquote {
    margin: 1em 0;
    padding-left: 1rem;
    border-left: 3px solid #e5e7eb;
    color: #4b5563;
  }

  .tiptap.ProseMirror code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    background: #f3f4f6;
    padding: 0.15em 0.3em;
    border-radius: 4px;
  }

  .tiptap.ProseMirror pre {
    font-family: inherit;
    background: #0b1120;
    color: #e5e7eb;
    padding: 12px 14px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 1em 0;
  }

  .tiptap.ProseMirror pre code {
    background: transparent;
    padding: 0;
  }

  .tiptap.ProseMirror hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 24px 0;
  }

  .tiptap.ProseMirror img {
    max-width: 100%;
    height: auto;
    display: block;
    border-radius: 4px;
    margin-top: 16px;
    margin-bottom: 16px;
  }

  .tiptap.ProseMirror table {
    border-collapse: collapse;
    margin: 0;
    table-layout: fixed;
    width: 100%;
    background-color: #fff;
  }

  /* Structural props that should never be overridden by SID controls */
  .tiptap.ProseMirror table td,
  .tiptap.ProseMirror table th {
    box-sizing: border-box;
    position: relative;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    transition: background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease;
  }

  /* Low-specificity defaults so SID style overrides take effect */
  :where(.tiptap.ProseMirror table td),
  :where(.tiptap.ProseMirror table th) {
    border: 1px solid #d9e1ea;
    background-color: #fff;
    padding: 6px 8px;
    vertical-align: top;
    text-align: left;
  }

  .tiptap.ProseMirror table td > *,
  .tiptap.ProseMirror table th > * {
    margin-top: 0;
    margin-bottom: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .tiptap.ProseMirror table td > p,
  .tiptap.ProseMirror table th > p {
    min-height: 1.25em;
  }

  /* Match ProseMirror table-resize behavior: columns without explicit colwidth keep a stable min width. */
  .tiptap.ProseMirror[contenteditable='true'] table td:not(.column-resize-dragging),
  .tiptap.ProseMirror[contenteditable='true'] table th:not(.column-resize-dragging) {
    min-width: var(--default-cell-min-width, 80px);
  }

  .tiptap.ProseMirror table th {
    font-weight: 600;
  }

  :where(.tiptap.ProseMirror table th) {
    background-color: #f8fafc;
  }

  .tiptap.ProseMirror[contenteditable='true'] table td:hover,
  .tiptap.ProseMirror[contenteditable='true'] table th:hover {
    background-color: #f8fafd;
  }

  .tiptap.ProseMirror[contenteditable='true'] table td:focus-within,
  .tiptap.ProseMirror[contenteditable='true'] table th:focus-within {
    background-color: #f8fbff;
    box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.22);
  }

  .tiptap.ProseMirror table .selectedCell {
    position: relative;
  }

  .tiptap.ProseMirror table .selectedCell::after {
    background: rgba(37, 99, 235, 0.12);
    box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.28);
    border-radius: 1px;
    content: '';
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    position: absolute;
    z-index: 2;
  }

  .tiptap.ProseMirror table .column-resize-handle {
    background-color: transparent;
    bottom: -2px;
    pointer-events: none;
    position: absolute;
    right: -2px;
    top: -1px;
    width: 3px;
    opacity: 0;
    z-index: 3;
    transition: opacity 120ms ease, background-color 120ms ease;
  }

  .tiptap.ProseMirror[contenteditable='true'] .tableWrapper:hover table .column-resize-handle {
    opacity: 0.2;
    background-color: rgba(148, 163, 184, 0.7);
  }

  .tiptap.ProseMirror[contenteditable='true'] table td:hover .column-resize-handle,
  .tiptap.ProseMirror[contenteditable='true'] table th:hover .column-resize-handle {
    opacity: 0.9;
    background-color: rgba(59, 130, 246, 0.45);
  }

  .tiptap.ProseMirror.resize-cursor table .column-resize-handle {
    opacity: 1;
    background-color: rgba(37, 99, 235, 0.55);
  }

  .tiptap.ProseMirror .tableWrapper {
    margin: 1.5rem 0;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 767px) {
    .tiptap.ProseMirror .tableWrapper {
      margin: 1rem 0;
    }

    /* Keep larger cells only for readonly rendering on mobile. */
    .tiptap.ProseMirror[contenteditable='false'] table td,
    .tiptap.ProseMirror[contenteditable='false'] table th {
      min-width: 120px;
    }
  }

  .tiptap.ProseMirror.resize-cursor {
    cursor: col-resize !important;
  }

  .tiptap.ProseMirror a {
    color: #2563eb;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .tiptap.ProseMirror mark {
    padding: 0 2px;
    border-radius: 2px;
  }

  .tiptap.ProseMirror span-var {
    display: inline-block;
    padding: 2px 4px;
    border-radius: 6px;
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: invert(0.15) grayscale(0.15);
    color: rgba(0, 0, 0, 0.75);
  }

  /* Image */
  &.vp-lg .tiptap.ProseMirror img[style*="--vp-img-width-lg"]  { width:  var(--vp-img-width-lg); }
  &.vp-md .tiptap.ProseMirror img[style*="--vp-img-width-md"]  { width:  var(--vp-img-width-md); }
  &.vp-sm .tiptap.ProseMirror img[style*="--vp-img-width-sm"]  { width:  var(--vp-img-width-sm); }

  &.vp-lg .tiptap.ProseMirror img[style*="--vp-img-height-lg"] { height: var(--vp-img-height-lg); }
  &.vp-md .tiptap.ProseMirror img[style*="--vp-img-height-md"] { height: var(--vp-img-height-md); }
  &.vp-sm .tiptap.ProseMirror img[style*="--vp-img-height-sm"] { height: var(--vp-img-height-sm); }

  &.vp-lg .tiptap.ProseMirror img[style*="--vp-img-ml-lg"] { margin-left: var(--vp-img-ml-lg); }
  &.vp-md .tiptap.ProseMirror img[style*="--vp-img-ml-md"] { margin-left: var(--vp-img-ml-md); }
  &.vp-sm .tiptap.ProseMirror img[style*="--vp-img-ml-sm"] { margin-left: var(--vp-img-ml-sm); }

  &.vp-lg .tiptap.ProseMirror img[style*="--vp-img-mr-lg"] { margin-right: var(--vp-img-mr-lg); }
  &.vp-md .tiptap.ProseMirror img[style*="--vp-img-mr-md"] { margin-right: var(--vp-img-mr-md); }
  &.vp-sm .tiptap.ProseMirror img[style*="--vp-img-mr-sm"] { margin-right: var(--vp-img-mr-sm); }

  &.vp-lg .tiptap.ProseMirror [data-node="image"][data-resize-container] { justify-content: var(--vp-img-jc-lg, flex-start) !important; }
  &.vp-md .tiptap.ProseMirror [data-node="image"][data-resize-container] { justify-content: var(--vp-img-jc-md, flex-start) !important; }
  &.vp-sm .tiptap.ProseMirror [data-node="image"][data-resize-container] { justify-content: var(--vp-img-jc-sm, flex-start) !important; }

  /* Table column width */
  &.vp-lg .tiptap.ProseMirror table col[style*="--table-col-width-"] {
    --table-col-width-current: var(--table-col-width-lg);
  }
  &.vp-md .tiptap.ProseMirror table col[style*="--table-col-width-"] {
    --table-col-width-current: var(--table-col-width-md, var(--table-col-width-lg));
  }
  &.vp-sm .tiptap.ProseMirror table col[style*="--table-col-width-"] {
    --table-col-width-current: var(--table-col-width-sm, var(--table-col-width-md, var(--table-col-width-lg)));
  }

  /* Table width */
  &.vp-lg .tiptap.ProseMirror table[style*="--vp-table-width-"] { width: var(--vp-table-width-lg); }
  &.vp-md .tiptap.ProseMirror table[style*="--vp-table-width-"] { width: var(--vp-table-width-md, var(--vp-table-width-lg)); }
  &.vp-sm .tiptap.ProseMirror table[style*="--vp-table-width-"] { width: var(--vp-table-width-sm, var(--vp-table-width-md, var(--vp-table-width-lg))); }

  /* Font size */
  &.vp-lg .tiptap.ProseMirror [style*="--vp-font-size-"] { font-size: var(--vp-font-size-lg); }
  &.vp-md .tiptap.ProseMirror [style*="--vp-font-size-"] { font-size: var(--vp-font-size-md, var(--vp-font-size-lg)); }
  &.vp-sm .tiptap.ProseMirror [style*="--vp-font-size-"] { font-size: var(--vp-font-size-sm, var(--vp-font-size-md, var(--vp-font-size-lg))); }

  /* Font weight */
  &.vp-lg .tiptap.ProseMirror [style*="--vp-font-weight-"] { font-weight: var(--vp-font-weight-lg); }
  &.vp-md .tiptap.ProseMirror [style*="--vp-font-weight-"] { font-weight: var(--vp-font-weight-md, var(--vp-font-weight-lg)); }
  &.vp-sm .tiptap.ProseMirror [style*="--vp-font-weight-"] { font-weight: var(--vp-font-weight-sm, var(--vp-font-weight-md, var(--vp-font-weight-lg))); }

  /* Letter spacing */
  &.vp-lg .tiptap.ProseMirror [style*="--vp-letter-spacing-"] { letter-spacing: var(--vp-letter-spacing-lg); }
  &.vp-md .tiptap.ProseMirror [style*="--vp-letter-spacing-"] { letter-spacing: var(--vp-letter-spacing-md, var(--vp-letter-spacing-lg)); }
  &.vp-sm .tiptap.ProseMirror [style*="--vp-letter-spacing-"] { letter-spacing: var(--vp-letter-spacing-sm, var(--vp-letter-spacing-md, var(--vp-letter-spacing-lg))); }

  /* Text align */
  &.vp-lg .tiptap.ProseMirror p[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror span[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror h1[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror h2[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror h3[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror h4[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror h5[style*="--vp-text-align-"],
  &.vp-lg .tiptap.ProseMirror h6[style*="--vp-text-align-"] {
    text-align: var(--vp-text-align-lg, start);
  }

  &.vp-md .tiptap.ProseMirror p[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror span[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror h1[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror h2[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror h3[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror h4[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror h5[style*="--vp-text-align-"],
  &.vp-md .tiptap.ProseMirror h6[style*="--vp-text-align-"] {
    text-align: var(--vp-text-align-md, var(--vp-text-align-lg, start));
  }

  &.vp-sm .tiptap.ProseMirror p[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror span[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror h1[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror h2[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror h3[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror h4[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror h5[style*="--vp-text-align-"],
  &.vp-sm .tiptap.ProseMirror h6[style*="--vp-text-align-"] {
    text-align: var(--vp-text-align-sm, var(--vp-text-align-md, var(--vp-text-align-lg, start)));
  }

  /* Line height */
  &.vp-lg .tiptap.ProseMirror [style*="--vp-line-height-"] { line-height: var(--vp-line-height-lg); }
  &.vp-md .tiptap.ProseMirror [style*="--vp-line-height-"] { line-height: var(--vp-line-height-md, var(--vp-line-height-lg)); }
  &.vp-sm .tiptap.ProseMirror [style*="--vp-line-height-"] { line-height: var(--vp-line-height-sm, var(--vp-line-height-md, var(--vp-line-height-lg))); }

  .tiptap.ProseMirror .ProseMirror-selectednode [data-resize-handle] {
    position: absolute;
    background: #FFFFFF;
    border-radius: 6px;
    z-index: 10;

    &[data-resize-handle=top],
    &[data-resize-handle=right],
    &[data-resize-handle=left],
    &[data-resize-handle=bottom] {
      border: 1.5px solid #62ad62;
    }

    &[data-resize-handle=top-left],
    &[data-resize-handle=top-right],
    &[data-resize-handle=bottom-left],
    &[data-resize-handle=bottom-right] {
      width: 9px;
      height: 9px;
      border: 1.5px solid #62ad62;
    }

    &[data-resize-handle=top] {
      top: 14px !important;
      cursor: ns-resize;
    }

    &[data-resize-handle=bottom] {
      bottom: 14px !important;
      cursor: ns-resize;
    }

    &[data-resize-handle=right] {
      top: 16px !important;
      height: calc(100% - 32px);
      cursor: ew-resize;
    }

    &[data-resize-handle=left] {
      top: 16px !important;
      height: calc(100% - 32px);
      cursor: ew-resize;
    }

    &[data-resize-handle=top-left] {
      top: 10px !important;
      left: -3px !important;
      cursor: nwse-resize;
    }

    &[data-resize-handle=top-right] {
      top: 10px !important;
      right: -3px !important;
      cursor: nesw-resize;
    }

    &[data-resize-handle=bottom-left] {
      bottom: 10px !important;
      left: -3px !important;
      cursor: nesw-resize;
    }

    &[data-resize-handle=bottom-right] {
      bottom: 10px !important;
      right: -3px !important;
      cursor: nwse-resize;
    }
  }

  [data-resize-state='true'] [data-resize-wrapper] {
    outline: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 0.125rem;
  }
}
</style>
